<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { copyToClipboard } from '@/utils/strings'

const props = defineProps({
  value: {
    type: [String, Number],
    default: '',
  },
  label: {
    type: String,
    default: undefined,
  },

  // One knob for the whole control - the field density, the button and the icon scale together
  size: {
    type: String,
    default: 'default',
    validator: value => ['xx-small', 'x-small', 'small', 'default', 'large'].includes(value),
  },
  tooltip: {
    type: String,
    default: undefined,
  },

  // Codes read better in a fixed-width face; URLs do not
  monospace: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const { t } = useI18n()

const SIZES = {
  // `height` opts the size into the sub-compact CSS below - Vuetify's density scale stops at 40px.
  // xx-small matches a `size="small"` VBtn (34px), so a field can sit in a row of them.
  'xx-small': { density: 'compact', button: 'x-small', icon: 16, height: '34px' },
  'x-small': { density: 'compact', button: 'x-small', icon: 14, height: '28px' },
  small: { density: 'compact', button: 'small', icon: 16 },
  default: { density: 'comfortable', button: 'default', icon: 20 },
  large: { density: 'default', button: 'large', icon: 24 },
}

const scale = computed(() => SIZES[props.size] ?? SIZES.default)

const displayValue = computed(() => String(props.value ?? ''))

const copy = () => copyToClipboard(displayValue.value, true)
</script>

<template>
  <VTextField
    :model-value="displayValue"
    :label="label"
    :density="scale.density"
    :disabled="disabled"
    class="copyable-field"
    :class="[monospace ? 'copyable-field--mono' : '', scale.height ? 'copyable-field--dense' : '']"
    :style="scale.height ? { '--copyable-field-height': scale.height } : undefined"
    readonly
    variant="outlined"
    color="secondary"
    hide-details
  >
    <template #append-inner>
      <VDivider vertical />
      <VBtn
        :size="scale.button"
        :disabled="!displayValue"
        variant="text"
        color="secondary"
        class="copyable-field__btn"
        @click="copy"
      >
        <VIcon
          icon="ri-file-copy-line"
          :size="scale.icon"
        />
        <VTooltip
          activator="parent"
          location="top"
        >
          {{ tooltip || $t('clipboard.copy') }}
        </VTooltip>
      </VBtn>
    </template>
  </VTextField>
</template>

<style scoped lang="scss">
// The button is flush with the right edge and the divider runs the full field height,
// so the append slot has to stretch instead of centring its content
.copyable-field :deep(.v-field__append-inner) {
  align-items: stretch;
  padding-block: 0;
  padding-inline-end: 0;

  // `disabled` marks the surrounding section as locked and kills pointer events on the
  // whole input - the value still has to be copyable, so the button opts back in
  pointer-events: auto;

  // Carries the field's own radius down to the button, whatever the density set it to
  border-radius: inherit;
}

.copyable-field :deep(.v-field) {
  padding-inline-end: 0;
}

// The template gives x-small buttons a flat 2px radius through a three-class selector
// (@core/scss/template/libs/vuetify/components/_button.scss) - out-specified here, or the
// hover overlay spills past the field's rounded corner
.copyable-field .copyable-field__btn.v-btn:not(.v-btn--icon) {
  align-self: stretch;
  block-size: auto;

  // Flush with the field edge, so the hover overlay has to follow the rounded corners
  // there and stay square where it meets the divider
  border-radius: inherit;
  border-start-start-radius: 0;
  border-end-start-radius: 0;
}

.copyable-field--mono :deep(.v-field__input) {
  font-family: monospace;
  letter-spacing: 0.06em;
}

// Vuetify's density scale bottoms out at `compact` (40px), so the sub-compact sizes go
// below it by hand. There is no room for a floating label at these heights - use them without one.
.copyable-field--dense {
  --v-input-control-height: var(--copyable-field-height);
}

.copyable-field--dense :deep(.v-field) {
  font-size: 0.75rem;
}

// The template pins every compact field to 40px in
// @core/scss/template/libs/vuetify/components/_field.scss with a 5-class selector,
// so going below it takes precedence this rule cannot reach any other way
.copyable-field--dense :deep(.v-field__input) {
  min-block-size: var(--copyable-field-height) !important;
  padding-block: 0;
  padding-inline: 8px 6px;
}
</style>
